import { ref, computed, watch, nextTick } from "vue";

// ===== 유효성 검사 함수들 =====

// 이메일 형식 검증
export const validateEmail = (email) => {
  if (!email) {
    return { valid: false, errorCode: "empty" };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, errorCode: "invalid" };
  }
  return { valid: true };
};

// 비밀번호 유효성 검사 (영문숫자 포함 8자 이상)
export const validatePassword = (password) => {
  if (!password) {
    return { valid: false, errorCode: "empty" };
  }
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const isLongEnough = password.length >= 8;
  if (!isLongEnough || !hasLetter || !hasNumber) {
    return { valid: false, errorCode: "invalid" };
  }
  return { valid: true };
};

// ===== 기본 폼 필드 Composable =====

// 개별 폼 필드의 상태와 유효성 검사를 관리하는 composable
export const useFormField = (initialValue = "", options = {}) => {
  const {
    validator = null,
    errorMessages = {},
    required = false,
    onValidate = null,
    onAuth = null,
    checkDuplicate = null,
  } = options;

  // ===== 상태 =====
  const value = ref(initialValue);
  const isError = ref(false);
  const errorCode = ref(null);
  const shakeKey = ref(0);
  const focusMoved = ref(false);
  const inputRef = ref(null);

  // ===== 계산된 값 =====
  const errorMessage = computed(() => {
    if (!errorCode.value) return "";
    return errorMessages[errorCode.value] || "";
  });

  // ===== 메서드 =====
  // 필드 유효성 검사
  const validate = () => {
    // 필수 입력 검사
    if (required && !value.value.trim()) {
      isError.value = true;
      errorCode.value = "empty";
      return false;
    }

    // 커스텀 validator 실행
    if (validator && value.value.trim()) {
      const result = validator(value.value);
      if (!result.valid) {
        isError.value = true;
        errorCode.value = result.errorCode;
        return false;
      }
    }

    // 추가 커스텀 검사
    if (onValidate) {
      const result = onValidate(value.value);
      if (!result.valid) {
        isError.value = true;
        errorCode.value = result.errorCode;
        return false;
      }
    }

    isError.value = false;
    errorCode.value = null;
    return true;
  };

  // 수동으로 에러 설정
  const setError = (code) => {
    isError.value = true;
    errorCode.value = code;
  };

  // blur 이벤트 핸들러 (유효성 검사 실패 시 자동 포커스)
  const handleBlur = async () => {
    const isValid = validate();
    if (!isValid && !focusMoved.value && inputRef.value) {
      await nextTick();
      inputRef.value.focus();
      focusMoved.value = true;
    } else if (isValid) {
      focusMoved.value = false;
    }
  };

  // 에러 상태 초기화
  const reset = () => {
    isError.value = false;
    errorCode.value = null;
    focusMoved.value = false;
  };

  // 값과 에러 상태 모두 초기화
  const clear = () => {
    value.value = "";
    reset();
  };

  // 인증 처리 (이메일 등)
  const handleAuth = async () => {
    // 기본 유효성 검사
    if (!validate()) {
      if (inputRef.value) {
        await nextTick();
        inputRef.value.focus();
      }
      return false;
    }

    // 인증 함수 실행
    if (onAuth) {
      try {
        const result = await onAuth(value.value);
        if (!result.success) {
          setError(result.errorCode || "auth-failed");
          if (inputRef.value) {
            await nextTick();
            inputRef.value.focus();
          }
          return false;
        }
        return true;
      } catch (error) {
        setError("auth-failed");
        if (inputRef.value) {
          await nextTick();
          inputRef.value.focus();
        }
        return false;
      }
    }

    return true;
  };

  // 중복 체크
  const handleDuplicateCheck = async () => {
    // 기본 유효성 검사
    if (!validate()) {
      return false;
    }

    // 중복 체크 함수 실행
    if (checkDuplicate) {
      try {
        const result = await checkDuplicate(value.value);
        if (!result.available) {
          setError("duplicate");
          return false;
        }
        return true;
      } catch (error) {
        console.error("Duplicate check error:", error);
        return false;
      }
    }

    return true;
  };

  // ===== Watchers =====
  // 에러 상태 변경 감지 (shake 애니메이션 트리거)
  watch(isError, (newVal, oldVal) => {
    if (newVal && !oldVal) {
      shakeKey.value++;
    }
  });

  // 값 변경 감지 (에러 상태 자동 재검증)
  watch(value, () => {
    if (focusMoved.value && value.value.trim()) {
      focusMoved.value = false;
    }
    if (isError.value) {
      // 값이 변경되면 자동으로 재검증
      validate();
    }
  });

  return {
    // 상태
    value,
    isError,
    errorCode,
    errorMessage,
    shakeKey,
    inputRef,
    focusMoved,
    // 메서드
    validate,
    setError,
    handleBlur,
    handleAuth,
    handleDuplicateCheck,
    reset,
    clear,
  };
};

// ===== 비밀번호 필드 Composables =====

// 비밀번호 필드 관리 composable (표시/숨김, 유효성 검사 포함)
export const usePasswordField = (initialValue = "", options = {}) => {
  const {
    required = false,
    errorMessages: userErrorMessages = {},
    validateOnInput = false,
  } = options;

  // 기본 에러 메시지와 사용자 정의 에러 메시지 병합
  const defaultErrorMessages = {
    empty: "비밀번호를 입력해주세요.",
    invalid: "비밀번호는 영문숫자 포함 8자 이상이어야 합니다.",
  };
  const errorMessages = { ...defaultErrorMessages, ...userErrorMessages };

  // 기본 필드 기능 상속
  const field = useFormField(initialValue, {
    required,
    validator: validatePassword,
    errorMessages,
  });

  // 비밀번호 표시/숨김 상태
  const showPassword = ref(false);
  // 포커스 상태
  const isFocused = ref(false);

  // 비밀번호 표시/숨김 토글
  const togglePassword = () => {
    showPassword.value = !showPassword.value;
  };

  // 포커스 이벤트 핸들러
  const handleFocus = () => {
    isFocused.value = true;
  };

  // blur 이벤트 핸들러 (유효성 검사 + 포커스 상태 업데이트)
  const handleBlur = async () => {
    isFocused.value = false;
    await field.handleBlur();
  };

  // 입력값 변경 시 즉시 유효성 검사 (옵션)
  if (validateOnInput) {
    watch(field.value, () => {
      if (field.value.value.trim()) {
        field.validate();
      }
    });
  }

  return {
    // 기본 필드 속성
    ...field,
    // 비밀번호 전용 속성
    showPassword,
    isFocused,
    // 비밀번호 전용 메서드
    togglePassword,
    handleFocus,
    handleBlur,
  };
};

// 비밀번호 확인 필드 관리 composable
export const usePasswordConfirmField = (
  initialValue = "",
  passwordField,
  options = {}
) => {
  const {
    errorMessages = {
      empty: "비밀번호를 입력해주세요.",
      mismatch: "비밀번호가 일치하지 않습니다.",
    },
  } = options;

  // 비밀번호 확인 필드 생성
  const confirmField = usePasswordField(initialValue, {
    required: false,
    errorMessages,
  });

  // 비밀번호 일치 여부 검사
  const validateMatch = () => {
    if (!confirmField.value.value) {
      return true; // 빈 값이면 검사 안 함
    }
    if (passwordField.value.value !== confirmField.value.value) {
      confirmField.isError.value = true;
      confirmField.errorCode.value = "mismatch";
      return false;
    }
    confirmField.isError.value = false;
    confirmField.errorCode.value = null;
    return true;
  };

  // blur 이벤트 핸들러 (일치 검사 포함)
  const handleBlur = async () => {
    confirmField.isFocused.value = false;
    validateMatch();
    if (
      confirmField.isError.value &&
      !confirmField.focusMoved.value &&
      confirmField.inputRef.value
    ) {
      await nextTick();
      confirmField.inputRef.value.focus();
      confirmField.focusMoved.value = true;
    } else if (!confirmField.isError.value) {
      confirmField.focusMoved.value = false;
    }
  };

  // 원본 비밀번호가 변경되면 확인 필드도 재검증
  watch(passwordField.value, () => {
    if (confirmField.value.value) {
      validateMatch();
    }
  });

  // 확인 비밀번호 입력 시 실시간 검증
  watch(confirmField.value, () => {
    if (confirmField.focusMoved.value) {
      confirmField.focusMoved.value = false;
    }
    if (confirmField.value.value) {
      validateMatch();
    } else if (confirmField.isError.value) {
      confirmField.isError.value = false;
      confirmField.errorCode.value = null;
    }
  });

  return {
    ...confirmField,
    validateMatch,
    handleBlur,
  };
};

// ===== 폼 전체 관리 Composables =====

// 폼 전체 유효성 검사 및 제출 관리 composable
export const useFormValidation = (fields = {}, options = {}) => {
  const { onSubmit = null, additionalValidations = [] } = options;

  // 폼 전체 유효성 검사 여부
  const isFormValid = computed(() => {
    // 모든 필드가 유효한지 확인
    const allFieldsValid = Object.values(fields).every((field) => {
      // 에러가 있으면 무효
      if (field.isError?.value) return false;
      // required 필드는 값이 있어야 함
      if (field.required && !field.value?.value?.trim()) return false;
      return true;
    });

    if (!allFieldsValid) return false;

    // 추가 유효성 검사 조건 확인
    const additionalValid = additionalValidations.every((validation) => {
      return validation.condition !== false;
    });

    return additionalValid;
  });

  // 특정 필드들이 변경되었는지 확인
  const hasChanges = (initialValues = {}) => {
    return Object.entries(fields).some(([fieldName, field]) => {
      const initialValue = initialValues[fieldName];
      const currentValue = field.value?.value;
      // undefined/null은 빈 문자열로 취급
      const initial = initialValue ?? "";
      const current = currentValue ?? "";
      return initial !== current;
    });
  };

  // 모든 필드 유효성 검사 실행
  const validateAll = () => {
    let isValid = true;
    let firstErrorField = null;

    // 모든 필드 검증
    Object.values(fields).forEach((field) => {
      if (field.validate && !field.validate()) {
        isValid = false;
        if (!firstErrorField) {
          firstErrorField = field;
        }
      }
    });

    // 추가 유효성 검사
    additionalValidations.forEach((validation) => {
      if (!validation.condition) {
        isValid = false;
        if (!firstErrorField && validation.field) {
          validation.field.setError?.(validation.errorCode);
          firstErrorField = validation.field;
        }
      }
    });

    return { isValid, firstErrorField };
  };

  // 폼 제출 처리
  const handleSubmit = async () => {
    const { isValid, firstErrorField } = validateAll();

    // 유효성 검사 실패 시 첫 번째 에러 필드로 포커스
    if (!isValid) {
      await nextTick();
      if (firstErrorField?.inputRef?.value) {
        firstErrorField.inputRef.value.focus();
      }
      return false;
    }

    // 제출 함수 실행
    if (onSubmit) {
      try {
        const values = Object.entries(fields).reduce((acc, [key, field]) => {
          acc[key] = field.value?.value;
          return acc;
        }, {});

        const result = await onSubmit(values);
        return result !== false;
      } catch (error) {
        console.error("Form submission error:", error);
        return false;
      }
    }

    return true;
  };

  // 모든 필드 초기화
  const resetAll = () => {
    Object.values(fields).forEach((field) => {
      field.reset?.();
    });
  };

  // 모든 필드 값 및 에러 초기화
  const clearAll = () => {
    Object.values(fields).forEach((field) => {
      field.clear?.();
    });
  };

  // 폼 값 가져오기
  const getValues = () => {
    return Object.entries(fields).reduce((acc, [key, field]) => {
      acc[key] = field.value?.value;
      return acc;
    }, {});
  };

  // 폼 값 설정하기
  const setValues = (values = {}) => {
    Object.entries(values).forEach(([key, value]) => {
      if (fields[key]?.value) {
        fields[key].value.value = value;
      }
    });
  };

  return {
    // 상태
    isFormValid,
    // 메서드
    hasChanges,
    validateAll,
    handleSubmit,
    resetAll,
    clearAll,
    getValues,
    setValues,
  };
};

// 변경 사항 추적 composable
export const useFormChanges = (fields = {}, initialValues = {}) => {
  // 개별 필드 변경 여부
  const fieldChanges = computed(() => {
    const changes = {};
    Object.entries(fields).forEach(([fieldName, field]) => {
      const initialValue = initialValues[fieldName] ?? "";
      const currentValue = field.value?.value ?? "";
      changes[fieldName] = initialValue !== currentValue;
    });
    return changes;
  });

  // 전체 변경 여부
  const hasAnyChanges = computed(() => {
    return Object.values(fieldChanges.value).some((changed) => changed);
  });

  // 변경된 필드 목록
  const changedFields = computed(() => {
    return Object.entries(fieldChanges.value)
      .filter(([, changed]) => changed)
      .map(([fieldName]) => fieldName);
  });

  return {
    fieldChanges,
    hasAnyChanges,
    changedFields,
  };
};

